import { FormikErrors, FormikTouched } from 'formik';
import { CovidAddress } from 'interfaces/common';
import { Props } from '@service-victoria/component-library/components/AddressLookup';
import { constants } from 'utils';
import * as Yup from 'yup';

function getDetailsTouched(touched: boolean): FormikTouched<Props.Interfaces.FullDetails> {
  return {
    fullAddress: touched,
    postCode: touched,
    state: touched,
    suburb: touched
  };
}

function getDetailsErrors(error: string): FormikErrors<Props.Interfaces.FullDetails> {
  return {
    fullAddress: error,
    postCode: error,
    state: error,
    suburb: error
  };
}

/**
 * Initial values object for <Address /> element
 */
export const initialAddressValues: Props.Interfaces.AddressData = {
  summaryData: {
    label: '',
    value: ''
  },
  fullDetails: { id: '', fullAddress: '', postCode: '', state: '', suburb: '' }
};

/**
 * Initial touched object for full <Address /> element
 * @param touched touched value to populate
 */
export const initialAddressTouched = (touched: boolean) => {
  return {
    fullDetails: getDetailsTouched(touched),
    /**
     * This can be of OptionType/number/string, hence we store a single boolean value in it for consistency across all different cases
     */
    summaryData: touched
  };
};

/**
 * Initial error object for <Address /> element
 * @param error error to populate
 */
export const initialAddressErrors = (error: string) => {
  return {
    fullDetails: getDetailsErrors(error),
    summaryData: error
  };
};

/**
 * Manual type values
 */
export type ManualValues = AddressFormValues;

/**
 * <Formik /> address type values defintion for a "single" type
 */
export type AddressFormValues = {
  address: Props.Interfaces.AddressData;
};

/**
 * standard <Formik /> values type definition for an array of <Address /> elements
 */
export type ArrayFormValues = {
  addresses: Props.Interfaces.AddressData[];
};

/**
 *  Checks whether an address is PO Box. Returns true the string contains "po box"
 */
const isPostOfficeBox = (addressString: string) => {
  const cleanedAddressString = addressString.toLowerCase().replace(/[^\w\s]/gi, '');
  return cleanedAddressString.indexOf('po box') >= 0;
};

/**
 * Address validation schema helpers
 */
export const addressSchema = Yup.object().shape<CovidAddress>({
  fullAddress: Yup.string()
    .required(constants.AddressValidationErrors.place)
    .test(
      'isPostOfficeBox',
      constants.AddressValidationErrors.noPostBox,
      function validate(address) {
        if (address) {
          return !isPostOfficeBox(address);
        } else {
          return true;
        }
      }
    ),
  postCode: Yup.string()
    .required(constants.AddressValidationErrors.place)
    .matches(constants.REGEX.POSTCODE_VIC, constants.AddressValidationErrors.postcodeVic)
    .min(4, constants.AddressValidationErrors.postcodeVic)
    .max(4, constants.AddressValidationErrors.postcodeVic),
  state: Yup.string()
    .test('isStateVictoria', constants.AddressValidationErrors.stateVic, value => {
      const state = value?.toLowerCase();
      return state === 'vic' || state === 'victoria';
    })
    .required(constants.AddressValidationErrors.place),
  suburb: Yup.string().required(constants.AddressValidationErrors.place)
});
